import { motion, useReducedMotion, type Easing } from "framer-motion";
import { useState, useRef, useEffect, useMemo } from "react";
import type { BackgroundMediaConfig, KenBurnsConfig } from "../types/narrative";
import { getResponsiveSrcSet } from "../utils/responsiveImage";

interface BackgroundMediaProps {
  media: string | BackgroundMediaConfig;
  sectionId: string;
}

/** Default Ken Burns settings — a single settle-in push, then hold */
const KB_DEFAULTS: Required<KenBurnsConfig> = {
  startPosition: { x: 30, y: 30 },
  endPosition: { x: 70, y: 70 },
  startScale: 1,
  endScale: 1.15,
  duration: 6,
  easing: [0.5, 0, 0.5, 1], // slow start, fast middle, slow finish
  loop: false,
};

/**
 * Resolve a kenBurns setting into a full config or null.
 * Images pan by default — only an explicit `kenBurns: false` opts out.
 * `undefined` (omitted) and `true` both use the defaults; an object merges over them.
 */
function resolveKenBurns(
  raw: boolean | KenBurnsConfig | undefined,
): Required<KenBurnsConfig> | null {
  if (raw === false) return null; // explicit opt-out
  if (raw === undefined || raw === true) return KB_DEFAULTS;
  return {
    startPosition: {
      x: raw.startPosition?.x ?? KB_DEFAULTS.startPosition.x,
      y: raw.startPosition?.y ?? KB_DEFAULTS.startPosition.y,
    },
    endPosition: {
      x: raw.endPosition?.x ?? KB_DEFAULTS.endPosition.x,
      y: raw.endPosition?.y ?? KB_DEFAULTS.endPosition.y,
    },
    startScale: raw.startScale ?? KB_DEFAULTS.startScale,
    endScale: raw.endScale ?? KB_DEFAULTS.endScale,
    duration: raw.duration ?? KB_DEFAULTS.duration,
    easing: raw.easing ?? KB_DEFAULTS.easing,
    loop: raw.loop ?? KB_DEFAULTS.loop,
  };
}

/** Convert a 0-100 focal point + scale to CSS transform-origin & scale */
function toTransformOrigin(pos: { x?: number; y?: number }): string {
  return `${pos.x ?? 50}% ${pos.y ?? 50}%`;
}

export function BackgroundMedia({ media, sectionId }: BackgroundMediaProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  // Extract config properties
  const mediaPath = typeof media === "string" ? media : media.src;
  const persist = typeof media === "object" ? (media.persist ?? false) : false;
  const opacity = typeof media === "object" ? (media.opacity ?? 1) : 1;
  const kenBurnsRaw = typeof media === "object" ? media.kenBurns : undefined;

  const isVideo = mediaPath.match(/\.(mp4|webm|mov)$/i);

  // Resolve Ken Burns config (images only)
  const kb = useMemo(
    () => (isVideo ? null : resolveKenBurns(kenBurnsRaw)),
    [isVideo, kenBurnsRaw],
  );

  // Responsive image srcSet (null for video / non-raster)
  const responsiveInfo = useMemo(
    () => (isVideo ? null : getResponsiveSrcSet(mediaPath)),
    [isVideo, mediaPath],
  );

  // Ken Burns-aware sizes: account for max zoom scale so the browser picks
  // a variant large enough to stay sharp when scaled up.
  // Cap at 300vw — beyond this the largest variant (2560w) covers any realistic display.
  const MAX_SIZES_VW = 300;
  const effectiveSizes = useMemo(() => {
    if (!kb) return "100vw";
    const maxScale = Math.max(kb.startScale, kb.endScale);
    // Round up to nearest 10vw for a cleaner sizes attribute
    const vw = Math.ceil((maxScale * 100) / 10) * 10;
    return `${Math.min(vw, MAX_SIZES_VW)}vw`;
  }, [kb]);

  // For video crossfade looping
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [isCrossfading, setIsCrossfading] = useState(false);
  const video1Ref = useRef<HTMLVideoElement>(null);
  const video2Ref = useRef<HTMLVideoElement>(null);

  // Start playing video immediately on mount (before fade-in completes)
  useEffect(() => {
    if (!isVideo) return;

    const video1 = video1Ref.current;
    if (video1) {
      // Reduced motion: load the video but freeze on the first frame — don't
      // autoplay. Pause and pin to the start so the first decoded frame shows
      // as a still. (Future: honor an author-configured freeze timestamp; see
      // the BackgroundMedia note in docs/plans/p0-2-reduced-motion.md.)
      if (prefersReducedMotion) {
        video1.pause();
        if (video1.currentTime !== 0) video1.currentTime = 0;
        return;
      }
      // Only start playing if video hasn't started yet (not paused and currentTime is 0)
      // This prevents restarting when persist is true
      if (video1.paused && video1.currentTime === 0) {
        video1.play().catch(() => {
          // Ignore autoplay errors
        });
      }
    }
  }, [isVideo, sectionId, prefersReducedMotion]);

  // Handle video crossfade loop (only if not persisting, and not under reduced
  // motion — a frozen first-frame video never loops).
  useEffect(() => {
    if (!isVideo || persist || prefersReducedMotion) return;

    const video1 = video1Ref.current;
    const video2 = video2Ref.current;
    if (!video1 || !video2) return;

    const handleTimeUpdate = async () => {
      const activeVideo = activeVideoIndex === 0 ? video1 : video2;
      const nextVideo = activeVideoIndex === 0 ? video2 : video1;

      // Start crossfade 1 seconds before end for smoother transition
      if (
        activeVideo.duration - activeVideo.currentTime <= 1 &&
        !isCrossfading
      ) {
        setIsCrossfading(true);

        // Reset next video to start and play
        nextVideo.currentTime = 0;
        await nextVideo.play();

        // Wait for crossfade duration (1 seconds)
        setTimeout(() => {
          // Pause the video that faded out
          activeVideo.pause();
          // Switch active video
          setActiveVideoIndex(activeVideoIndex === 0 ? 1 : 0);
          setIsCrossfading(false);
        }, 1000);
      }
    };

    const activeVideo = activeVideoIndex === 0 ? video1 : video2;
    activeVideo.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      activeVideo.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [isVideo, activeVideoIndex, isCrossfading, persist, prefersReducedMotion]);

  return (
    <div
      className="absolute inset-0 overflow-hidden bg-black"
      style={{ opacity }}
    >
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black/60 z-1" />

      {isVideo ? (
        <>
          {/* Video 1 - starts playing */}
          <video
            ref={video1Ref}
            key={`${sectionId}-video1`}
            src={mediaPath}
            muted
            playsInline
            preload="auto"
            onLoadedData={() => setIsLoaded(true)}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1000ms] ease-in-out"
            style={{ opacity: activeVideoIndex === 0 ? 1 : 0 }}
          />
          {/* Video 2 - for crossfade looping (not needed when persisting, or
              when reduced motion freezes the first frame). */}
          {!persist && !prefersReducedMotion && (
            <video
              ref={video2Ref}
              key={`${sectionId}-video2`}
              src={mediaPath}
              muted
              playsInline
              preload="auto"
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1000ms] ease-in-out"
              style={{ opacity: activeVideoIndex === 1 ? 1 : 0 }}
            />
          )}
        </>
      ) : kb && !prefersReducedMotion ? (
        /* Ken Burns: continuous pan-and-zoom via Framer Motion.
         * Gated on !prefersReduced because reducedMotion="user" only suppresses
         * `scale` (a transform) — `transformOrigin` is not classified as a
         * transform, so its pan keyframes would keep running. When reduced
         * motion is requested we fall through to the static branch. */
        <motion.div
          key={sectionId}
          initial={{
            opacity: 0,
            scale: kb.startScale,
            transformOrigin: toTransformOrigin(kb.startPosition),
          }}
          animate={{
            opacity: isLoaded ? 1 : 0,
            scale: [kb.startScale, kb.endScale],
            transformOrigin: [
              toTransformOrigin(kb.startPosition),
              toTransformOrigin(kb.endPosition),
            ],
          }}
          transition={{
            opacity: { duration: 0.8, ease: "easeOut" },
            scale: {
              duration: kb.duration,
              ease: kb.easing as Easing,
              repeat: kb.loop ? Infinity : 0,
              repeatType: "reverse",
            },
            transformOrigin: {
              duration: kb.duration,
              ease: kb.easing as Easing,
              repeat: kb.loop ? Infinity : 0,
              repeatType: "reverse",
            },
          }}
          className="absolute inset-0 w-full h-full"
        >
          {responsiveInfo ? (
            <picture>
              <source
                type="image/webp"
                srcSet={responsiveInfo.srcSet}
                sizes={effectiveSizes}
              />
              <img
                src={responsiveInfo.fallbackSrc}
                alt=""
                onLoad={() => setIsLoaded(true)}
                className="w-full h-full object-cover"
              />
            </picture>
          ) : (
            <img
              src={mediaPath}
              alt=""
              onLoad={() => setIsLoaded(true)}
              className="w-full h-full object-cover"
            />
          )}
        </motion.div>
      ) : kb && prefersReducedMotion ? (
        /* Ken Burns: when reduced motion is requested we directly
         * go to the end position. */
        <motion.div
          key={sectionId}
          initial={{
            opacity: 0,
            scale: kb.startScale,
            transformOrigin: toTransformOrigin(kb.endPosition),
          }}
          animate={{
            opacity: isLoaded ? 1 : 0,
            scale: [kb.startScale, kb.endScale],
          }}
          transition={{
            opacity: { duration: 0.8, ease: "easeOut" },
            scale: {
              duration: kb.duration,
              ease: kb.easing as Easing,
              repeat: kb.loop ? Infinity : 0,
              repeatType: "reverse",
            },
          }}
          className="absolute inset-0 w-full h-full"
        >
          {responsiveInfo ? (
            <picture>
              <source
                type="image/webp"
                srcSet={responsiveInfo.srcSet}
                sizes={effectiveSizes}
              />
              <img
                src={responsiveInfo.fallbackSrc}
                alt=""
                onLoad={() => setIsLoaded(true)}
                className="w-full h-full object-cover"
              />
            </picture>
          ) : (
            <img
              src={mediaPath}
              alt=""
              onLoad={() => setIsLoaded(true)}
              className="w-full h-full object-cover"
            />
          )}
        </motion.div>
      ) : (
        /* Static image with optional responsive variants */
        <motion.div
          key={sectionId}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{
            opacity: isLoaded ? 1 : 0,
            scale: 1,
          }}
          transition={{
            opacity: { duration: 0.8, ease: "easeOut" },
            scale: { duration: 1.2, ease: "easeOut" },
          }}
          className="absolute inset-0 w-full h-full"
        >
          {responsiveInfo ? (
            <picture>
              <source
                type="image/webp"
                srcSet={responsiveInfo.srcSet}
                sizes="100vw"
              />
              <img
                src={responsiveInfo.fallbackSrc}
                alt=""
                onLoad={() => setIsLoaded(true)}
                className="w-full h-full object-cover"
              />
            </picture>
          ) : (
            <img
              src={mediaPath}
              alt=""
              onLoad={() => setIsLoaded(true)}
              className="w-full h-full object-cover"
            />
          )}
        </motion.div>
      )}
    </div>
  );
}
